import { Component, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  faChevronLeft,
  faMagnifyingGlassPlus,
  faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeft,
  faXmark,
  faArrowUp,
} from '@fortawesome/pro-light-svg-icons';
import { SalesReportService } from '../../services/sales-report.service';
import { Location, DatePipe } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-cash-register-report',
  templateUrl: './cash-register-report.component.html',
  styleUrls: ['./cash-register-report.component.scss'],
})
export class CashRegisterReportComponent implements OnDestroy {
  sessionsData: any = [];
  faMagnifyinGlassPlus = faMagnifyingGlassPlus;
  faMagnifyingGlass = faMagnifyingGlass;
  faChevronLeft = faChevronLeft;
  faArrowLeft = faArrowLeft;
  faXmark = faXmark;
  faArrowUp = faArrowUp;
  showScrollTop = false;

  today = new Date();
  currentPage = 1;
  lastPage = 1;
  itemsPerPage = 15;
  companyId!: string | number;
  loading = false;
  loadingMore = false;
  private scrollTimeout: any = null;
  private destroy$ = new Subject<void>();

  filterForm = new FormGroup({
    sessionSearch: new FormControl(''),
    dateFrom: new FormControl<Date | null>(null),
    dateTo: new FormControl<Date | null>(null),
  });

  constructor(
    private salesReportService: SalesReportService,
    private router: Router,
    private location: Location,
    private datePipe: DatePipe,
  ) {
    const id_company = localStorage.getItem('id_business') || '';
    this.companyId = id_company;
    this.getData();

    this.filterForm.get('sessionSearch')!.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$),
    ).subscribe(() => this.resetAndLoad());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dateRangeLabel(): string {
    const from = this.filterForm.get('dateFrom')!.value;
    const to = this.filterForm.get('dateTo')!.value;
    if (!from) return 'Fecha';
    const fromStr = this.datePipe.transform(from, 'dd/MM/yyyy')!;
    const toStr = to ? this.datePipe.transform(to, 'dd/MM/yyyy')! : null;
    if (!toStr || toStr === fromStr) return fromStr;
    return `${fromStr} - ${toStr}`;
  }

  private dateSnapshotFrom: Date | null = null;
  private dateSnapshotTo: Date | null = null;

  onPickerOpen() {
    this.dateSnapshotFrom = this.filterForm.get('dateFrom')!.value;
    this.dateSnapshotTo = this.filterForm.get('dateTo')!.value;
  }

  onDateChange() {
    const from = this.filterForm.get('dateFrom')!.value;
    const to = this.filterForm.get('dateTo')!.value;
    const sameFrom = this.dateSnapshotFrom?.getTime() === from?.getTime();
    const sameTo = this.dateSnapshotTo?.getTime() === to?.getTime();
    if (sameFrom && sameTo) return;
    this.dateSnapshotFrom = from;
    this.dateSnapshotTo = to;
    this.resetAndLoad();
  }

  resetAndLoad() {
    this.currentPage = 1;
    this.lastPage = 1;
    this.sessionsData = [];
    this.loading = true;
    this.getData();
  }

  clearFilters() {
    this.filterForm.reset({ sessionSearch: '', dateFrom: null, dateTo: null }, { emitEvent: false });
    this.resetAndLoad();
  }

  get hasActiveFilters(): boolean {
    const { sessionSearch, dateFrom, dateTo } = this.filterForm.value;
    return !!(sessionSearch || dateFrom || dateTo);
  }

  private getFilterParams(): { dateFrom?: string; dateTo?: string; sessionSearch?: string } {
    const { sessionSearch, dateFrom, dateTo } = this.filterForm.value;
    const effectiveDateTo = dateFrom && !dateTo ? dateFrom : dateTo;
    return {
      dateFrom: dateFrom ? this.datePipe.transform(dateFrom, 'yyyy-MM-dd')! : undefined,
      dateTo: effectiveDateTo ? this.datePipe.transform(effectiveDateTo, 'yyyy-MM-dd')! : undefined,
      sessionSearch: sessionSearch || undefined,
    };
  }

  getData() {
    const page = this.currentPage;
    if (page === 1) { this.loading = true; }
    const { dateFrom, dateTo, sessionSearch } = this.getFilterParams();
    this.salesReportService.getListsSessions(+this.companyId, page, this.itemsPerPage, dateFrom, dateTo, sessionSearch).subscribe((res: any) => {
      if (res) {
        this.lastPage = res.data.pagination.last_page || 1;
        if (page === 1) {
          this.sessionsData = res.data.data;
        } else {
          this.sessionsData = [...this.sessionsData, ...res.data.data];
        }
        this.loading = false;
        this.loadingMore = false;
      }
    });
  }

  viewSession(id: number) {
    this.router.navigateByUrl('/cash-register-report/' + id);
  }
  goBack() {
    if (
      localStorage.getItem('id_work_time') &&
      localStorage.getItem('id_cash_register')
    ) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/select-cashregister');
    }
  }

  checkNameCashRegister(session: any) {
    if (!session) {
      return false;
    } else {
      return true;
    }
  }

  formatNumber(floatValue = 1, decimals = 2, multiplier = 1) {
    let floatMultiplied = floatValue * multiplier;
    let stringFloat = floatMultiplied + '';
    let arraySplitFloat = stringFloat.split('.');
    let decimalsValue = '0';
    if (arraySplitFloat.length > 1) {
      decimalsValue = arraySplitFloat[1].slice(0, decimals);
    }
    let integerValue = arraySplitFloat[0];
    let arrayFullStringValue = [integerValue, decimalsValue];
    let FullStringValue = arrayFullStringValue.join('.');
    let floatFullValue = parseFloat(FullStringValue) + '';
    let formatFloatFullValue = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      style: 'currency',
      currency: 'EUR',
    }).format(Number(floatFullValue));
    return formatFloatFullValue;
  }

  @HostListener('window:scroll')
  onScroll() {
    this.showScrollTop = window.pageYOffset > 300;

    if (this.loadingMore || this.currentPage >= this.lastPage) {
      return;
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      const scrollPosition = window.pageYOffset + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      if (scrollPosition >= docHeight - 200) {
        this.loadingMore = true;
        this.currentPage++;
        this.getData();
      }
    }, 300);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
